<template>
  <div v-if="bannerData && !isClosed">
    <div class="new-promo-banner" :class="['new-promo-banner--' + bannerData.template]">
      
      <portal to="dash-modal">
        <PromoModalNew
          v-if="opensModal && showBannerModal"
          :theme="bannerData.template"
          :modal-message="modalMessage"
          @close="showBannerModal = false"
        />
      </portal>

      <GekkoButton
        v-if="isOpen && isCloseable"
        @click.prevent="closeBanner"
        icon="fa-solid fa-xmark"
        variant="link"
        size="lg"
        class="close-btn text-unassigned"
      />

      <div @click="openBannerModal" class="banner--content">
        <iframe
          v-if="bannerDocument"
          ref="bannerFrame"
          class="promo-banner-frame"
          :class="{ 'prevent-interaction': opensModal }"
          sandbox="allow-scripts allow-popups allow-same-origin allow-top-navigation allow-top-navigation-by-user-activation"
          :srcdoc="bannerDocument"
          @load="handleFrameLoad"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import GekkoButton from '@/components/GekkoButton'
import PromoModalNew from '@/components/dashboard/ui/PromoModalNew'
export default {
  name: 'PromoBannerNew',
  components: {
    GekkoButton,
    PromoModalNew,
  },
  props: {
    bannerData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isOpen: false,
      isClosed: false,
      showBannerModal: false,
      loading: false,
    }
  },
  computed: {
    ...mapGetters({
      preferredLanguage: 'main/getPreferredLanguage',
    }),
    bannerMessage() {
      return (
        (this.bannerData && this.bannerData.html_content) || ''
      )
    },
    modalMessage() {
      return (
        (this.bannerData &&
          this.bannerData.modal_html_content) ||
        ''
      )
    },
    isCloseable() {
      return !!(this.bannerData && this.bannerData.is_closeable)
    },
    opensModal() {
      return !!(this.bannerData && this.bannerData.opens_modal)
    },
    bannerDocument() {
      const raw = this.bannerMessage || ''

      if (!raw) return ''

      if (/<html[\s>]/i.test(raw)) {
        return raw
      }

      return `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1">
            <style>
              html, body { margin: 0; padding: 0; background: transparent; }
            </style>
          </head>
          <body>${raw}</body>
        </html>
      `
    },
  },
  watch: {
    bannerData: {
      immediate: true,
      handler(val) {
        this.showBannerModal = false
        this.isOpen = false
        if (!val) {
          this.isClosed = false
          return
        }
        setTimeout(() => { this.isOpen = true }, 1000)
        this.applyClosedFromStorage()
      },
    },
  },
  mounted() {
    window.addEventListener('resize', this.resizeFrame)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeFrame)
  },
  methods: {
    openBannerModal(e) {
      if (!this.bannerData) return
      if (this.opensModal) {
        e.preventDefault()
        this.showBannerModal = true
      }
    },
    applyClosedFromStorage() {
      // only do anything once banner exists
      if (!this.bannerData) return

      // ONLY check storage if banner is closeable
      if (!this.isCloseable) {
        this.isClosed = false
        return
      }

      try {
        this.isClosed = localStorage.getItem(this.getBannerClosedKey()) === '1'
      } catch (e) {
        // storage blocked -> treat as not closed
        this.isClosed = false
      }
    },
    getBannerClosedKey() {
      // unique per banner
      var id = this.bannerData && this.bannerData.id ? this.bannerData.id : 'unknown'
      return 'promo_banner_closed:' + id
    },
    closeBanner() {
      this.isClosed = true
      try {
        localStorage.setItem(this.getBannerClosedKey(), '1')
      } catch (e) {
        console.log('error')
      }
    },
    resizeFrame() {
      const iframe = this.$refs.bannerFrame
      if (!iframe) return

      try {
        const doc = iframe.contentDocument
        if (!doc) return

        const body = doc.body
        const docEl = doc.documentElement

        iframe.style.height = '0px'
        iframe.style.height = `${Math.max(
          100,
          body ? body.scrollHeight : 0,
          body ? body.offsetHeight : 0,
          docEl ? docEl.scrollHeight : 0,
          docEl ? docEl.offsetHeight : 0
        )}px`
      } catch (e) {
        // iframe sizing is best-effort
      }
    },
    handleFrameLoad() {
      this.resizeFrame()
    },
  },
}
</script>
<style lang="scss">
.promo-banner-frame {
  width: 100%;
  min-height: 100px;
  border: 0;
  display: block;
  background: transparent;
  &.prevent-interaction {
    pointer-events: none;
  }
}
.new-promo-banner {
  cursor: pointer;
  display: flex;
  align-items: center;
  height: auto;
  transition: height 0.8s cubic-bezier(0.645, 0.045, 0.355, 1);
  overflow: visible;
  position: relative;
  .close-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
    z-index: 9001;
    padding: 0.25rem 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: white !important;
    background: transparent;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0px;
  }
  .banner--content {
    &:hover {
      text-decoration: none;
    }
    border-radius: 12px;
    display: flex;
    font-weight: 900;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    max-width: 100%;
    height: 100%;
    min-height: 100px;
    overflow: hidden;
    transition: all 0.5s cubic-bezier(0.75, 0.02, 0.19, 1.29);
  }
  // &.open {
  //   height: auto;
  //   .banner--content {
  //     opacity: 1;
  //     transform: scale(1);
  //     transition-delay: 0.2s;
  //   }
  // }
  // @media (min-width: 640px) {
  //   &.open {
  //     height: auto;
  //   }
  // }
}
</style>
